import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, inject, viewChild } from '@angular/core';
import { OpenerSchemeRewardRule, BottleProductInfo } from 'src/app/api/api-campaigns.service.generated';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { BottleProduct, BottleProductsICClient } from 'src/app/api/api-image-classification.service.generated';
import { concat, Observable, of, lastValueFrom } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@angular/flex-layout';
import { LoadingOverlayComponent, ButtonComponent } from 'wecheer-common';

@Component({
  selector: 'app-opener-scheme-rule-edit',
  templateUrl: './opener-scheme-rule-edit.component.html',
  styleUrls: ['./opener-scheme-rule-edit.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatTooltipModule,
    FlexLayoutModule,
    LoadingOverlayComponent,
    ButtonComponent
]
})
export class OpenerSchemeRuleEditComponent implements OnChanges {
  private bottleProductsClient = inject(BottleProductsICClient);

  @Input() ruleIndex: number;
  @Input() rule: OpenerSchemeRewardRule;
  @Output() onSaveRule = new EventEmitter<SavedRule>();
  @Output() onRemoveRule = new EventEmitter<number>();
  allBottleProducts: BottleProduct[] = [];
  form: FormGroup
  isSetFormValueDone = false;
  selectedProductIds: string[] = [];
  selectedProducts: BottleProduct[] = [];

  readonly bottleProdIdInput = viewChild<ElementRef<HTMLInputElement>>('bottleProdIdInput');
  readonly matAutocomplete = viewChild<MatAutocomplete>('auto');
  readonly matAutocompleteTrigger = viewChild<MatAutocompleteTrigger>('trigger');
  addOnBlur = false;
  bottleProdIdCtrl = new FormControl();
  separatorKeysCodes: number[] = [13, 188];
  filteredBottleProducts: Observable<BottleProduct[]>;
  saveStatus: number = 0; // 0: idle, 1: saving, 2: saved

  constructor() {
    this.form = new FormGroup({
      pointsPerBottle: new FormControl('', Validators.required)
    });
    //this.allBottleProducts = this.bottleProductsClient.getAllBottleProducts();
    this.filteredBottleProducts = concat(of(this.bottleProdIdCtrl.value), this.bottleProdIdCtrl.valueChanges).pipe(
      debounceTime(100),
      switchMap(input => of(this.allBottleProducts.filter(x => x.name.toLocaleLowerCase().indexOf(input) >= 0))));

    this.form.valueChanges.subscribe(() => {
      this.saveStatus = 0;
    });
    this.bottleProdIdCtrl.valueChanges.subscribe(() => {
      this.saveStatus = 0;
    });
  }

  async ngOnChanges() {
    if (!this.isSetFormValueDone) {
  this.allBottleProducts = await lastValueFrom(this.bottleProductsClient.getAllBottleProducts());
      this.setFormValue();
      this.isSetFormValueDone = true;
    }
  }

  setFormValue() {
    this.form.patchValue({
      pointsPerBottle: this.rule.pointsPerBottle
    });
    if (this.rule.bottleProducts) {
      this.rule.bottleProducts.forEach(x => this.selectedProductIds.push(x.id));
    }
    this.bindSelectedProducts();
    this.saveRule(true);
  }

  bindSelectedProducts() {
    this.selectedProducts = [];
    this.selectedProductIds.forEach(id => {
      const prod = this.allBottleProducts.find(p => p.id === id);
      if (prod) this.selectedProducts.push(prod);
    });
  }

  onSubmit() { } // do we really need this?

  saveRule(init: boolean = false) {
    this.saveStatus = 1;
    const savedRule = new SavedRule();
    savedRule.index = this.ruleIndex;
    savedRule.rule = new OpenerSchemeRewardRule({
      pointsPerBottle: this.form.value.pointsPerBottle,
      // brands: this.selectedProductIds
      bottleProducts: this.selectedProductIds.map(x => {
        const prod = this.allBottleProducts.find(p => p.id === x);
        return new BottleProductInfo({
          id: x,
          bottleCapIds: prod.bottleCapIds,
          description: prod.description,
          imageUrl: prod.imageUrl,
          name: prod.name
        });
      })
    });
    this.onSaveRule.next(savedRule);
    setTimeout(() => {
      this.saveStatus = init ? 0 : 2;
    }, 300);
  }

  removeRule() {
    this.onRemoveRule.next(this.ruleIndex);
  }

  add(event: MatChipInputEvent): void {
    // Add label only when MatAutocomplete is not open to make sure this does not conflict with OptionSelected Event
    if (this.matAutocomplete().isOpen)
      return;

    const input = event.input;
    const value = event.value;
    const label = (value || '').trim();
    const prod = this.allBottleProducts.find(x => x.name === label);

    if (prod) {
      const alreadyAdded = this.selectedProductIds.filter(x => x === prod.id).length > 0;
      if (!alreadyAdded) {
        this.selectedProductIds.push(prod.id);
        this.bindSelectedProducts();
      }
    }

    // Reset the input value
    if (input)
      input.value = '';

    this.bottleProdIdCtrl.setValue(null);
    this.bottleProdIdInput().nativeElement.blur();
    this.matAutocompleteTrigger().closePanel();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const label = event.option.viewValue;
    const prod = this.allBottleProducts.find(x => x.name === label);
    const alreadyAdded = this.selectedProductIds.filter(x => x === prod.id).length > 0;
    if (!alreadyAdded) {
      this.selectedProductIds.push(prod.id);
      this.bindSelectedProducts();
    }

    this.bottleProdIdInput().nativeElement.value = '';
    this.bottleProdIdInput().nativeElement.blur();
    this.bottleProdIdCtrl.setValue(null);
  }

  onFocus(_e: any) {
    this.bottleProdIdCtrl.patchValue('');
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    event.clipboardData
      .getData('Text')                       // gets the text pasted
      .split(/;|,|\n/)                       // splits it when a SEMICOLON or COMMA or NEWLINE or SPACE
      .forEach(value => {
        if (value.trim()) {                 // push if valid
          this.selectedProductIds.push(value.trim());
        }
      });
  }

  remove(index: number) {
    this.selectedProductIds.splice(index, 1);
    this.selectedProducts.splice(index, 1);
    this.bottleProdIdInput().nativeElement.blur();
    this.saveStatus = 0;
  }
}

export class SavedRule {
  public rule: OpenerSchemeRewardRule;
  public index: number;
}
